import HttpService from './HttpService';

class Model3DService {
  constructor() {
    this.api = HttpService;
  }

  /**
   * Get 3D models for the current user with pagination
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default 1)
   * @param {number} options.per_page - Items per page (default 50, max 100)
   * @param {number} options.house_id - Filter by house
   * @param {number} options.room_id - Filter by room
   * @param {string} options.category - Filter by category
   * @param {string} options.format - Filter by format
   * @returns {Promise<{models: Array, pagination: Object}>}
   */
  async getModels(options = {}) {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.per_page) params.append('per_page', options.per_page);
    if (options.house_id) params.append('house_id', options.house_id);
    if (options.room_id) params.append('room_id', options.room_id);
    if (options.category) params.append('category', options.category);
    if (options.format) params.append('format', options.format);

    const queryString = params.toString();
    const url = queryString ? `/models3d?${queryString}` : '/models3d';
    const response = await this.api.get(url);
    return response.data;
  }

  /**
   * Get a specific 3D model by ID
   */
  async getModel(id) {
    const response = await this.api.get(`/models3d/${id}`);
    return response.data;
  }

  /**
   * Upload a new 3D model
   * @param {File} file - The 3D model file
   * @param {Object} metadata - Model metadata (name, description, category, etc.)
   */
  async uploadModel(file, metadata = {}) {
    const formData = new FormData();
    formData.append('file', file);

    if (metadata.name) formData.append('name', metadata.name);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.category) formData.append('category', metadata.category);
    if (metadata.house_id) formData.append('house_id', metadata.house_id);
    if (metadata.room_id) formData.append('room_id', metadata.room_id);
    if (metadata.default_scale !== undefined) formData.append('default_scale', metadata.default_scale);
    if (metadata.default_position_x !== undefined) formData.append('default_position_x', metadata.default_position_x);
    if (metadata.default_position_y !== undefined) formData.append('default_position_y', metadata.default_position_y);
    if (metadata.default_position_z !== undefined) formData.append('default_position_z', metadata.default_position_z);

    const response = await this.api.post('/models3d', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Update 3D model metadata
   */
  async updateModel(id, data) {
    const response = await this.api.put(`/models3d/${id}`, data);
    return response.data;
  }

  /**
   * Delete a 3D model
   */
  async deleteModel(id) {
    const response = await this.api.delete(`/models3d/${id}`);
    return response.data;
  }

  /**
   * Get presigned download URL for a 3D model (for direct downloads)
   */
  async getDownloadUrl(id) {
    const response = await this.api.get(`/models3d/${id}/download`);
    return response.data;
  }

  /**
   * Get the proxy URL for streaming a 3D model file through the backend.
   * This avoids CORS issues when loading models in the Three.js viewer.
   * @param {number} id - Model ID
   * @returns {string} The proxy URL for the model file
   */
  getFileUrl(id) {
    // Get the base URL from the HttpService or use default
    const baseUrl = this.api.defaults?.baseURL || '/api/v1';
    return `${baseUrl}/models3d/${id}/file`;
  }

  /**
   * Fetch the 3D model file and return a blob URL.
   * This allows Three.js loaders to access the file with proper authentication.
   * @param {number} id - Model ID
   * @returns {Promise<string>} Blob URL that can be used by Three.js loaders
   */
  async getFileBlobUrl(id) {
    const response = await this.api.get(`/models3d/${id}/file`, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  }

  /**
   * Get available model categories
   */
  async getCategories() {
    const response = await this.api.get('/models3d/categories');
    return response.data;
  }

  /**
   * Get storage usage and quota information
   * @returns {Promise<Object>} Storage usage info including used_mb, quota_mb, percentage
   */
  async getStorageUsage() {
    const response = await this.api.get('/models3d/usage');
    return response.data;
  }

  /**
   * Get supported 3D model formats
   */
  async getFormats() {
    const response = await this.api.get('/models3d/formats');
    return response.data;
  }

  /**
   * Helper to format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get format display info
   */
  getFormatInfo(format) {
    const formats = {
      glb: { name: 'GLB (Binary glTF)', icon: '3d_rotation', color: 'text-blue-600' },
      gltf: { name: 'glTF', icon: '3d_rotation', color: 'text-blue-500' },
      obj: { name: 'Wavefront OBJ', icon: 'view_in_ar', color: 'text-gray-600' },
      fbx: { name: 'Autodesk FBX', icon: 'view_in_ar', color: 'text-orange-600' },
      usdz: { name: 'Apple USDZ', icon: 'view_in_ar', color: 'text-purple-600' },
      splat: { name: 'Gaussian Splat', icon: 'blur_on', color: 'text-green-600' },
      ply: { name: 'PLY Point Cloud', icon: 'scatter_plot', color: 'text-teal-600' }
    };
    return formats[format] || { name: format.toUpperCase(), icon: 'view_in_ar', color: 'text-gray-500' };
  }

  /**
   * Check if a file extension is supported
   */
  isSupported(filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['glb', 'gltf', 'obj', 'fbx', 'usdz', 'splat', 'ply'].includes(ext);
  }

  /**
   * Get file extension from filename
   */
  getExtension(filename) {
    return filename.split('.').pop()?.toLowerCase() || '';
  }
}

export default new Model3DService();
